/**
 * ProposalTable — Displays AI-proposed jobs for review.
 *
 * Shows each proposed job with: job_name, load_strategy chip, column count.
 * Per-job Accept button accepts ONLY that job (POST .../accept-item) and
 * removes it from the list, leaving the rest pending.
 * "Accept All" bulk button accepts every remaining proposed job in one call
 * (POST .../accept).
 * "Done" (shown once at least one job has been individually accepted) closes
 * out the rest of the proposal without creating them (POST .../reject under
 * the hood) and finishes the wizard with the jobs accepted so far.
 * "Reject" button calls POST /proposals/{id}/reject → returns to wizard step 1.
 *
 * Safety: renders only job metadata from proposal items — job names, load
 * strategies, and column counts. No actual column values or source data.
 */

import { useCallback, useState } from "react";
import { Check, X, Loader2, CheckCheck } from "lucide-react";
import type {
  PEProposal,
  ProposedJobItem,
  LoadStrategy,
} from "../../features/pipeline-engineer/types";
import {
  acceptProposal,
  acceptProposalItem,
  rejectProposal,
} from "../../features/pipeline-engineer/api/pe-client";

// ---------------------------------------------------------------------------
// Design system chips (matches JobList.tsx patterns)
// ---------------------------------------------------------------------------

const STRATEGY_CHIP: Record<LoadStrategy, string> = {
  full_replace: "bg-sky-500/10 text-sky-400 border border-sky-500/20",
  incremental_merge:
    "bg-green-500/10 text-green-400 border border-green-500/20",
  scd1: "bg-violet-500/10 text-violet-400 border border-violet-500/20",
  scd2: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
};

const STRATEGY_LABEL: Record<LoadStrategy, string> = {
  full_replace: "Full Replace",
  incremental_merge: "Incremental",
  scd1: "SCD1",
  scd2: "SCD2",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ProposalTableProps {
  proposal: PEProposal & { items: ProposedJobItem[] };
  /**
   * Called once the user is finished with this proposal and every job they
   * want has been created — via every item individually accepted, Accept
   * All, or Done (finish now, skip whatever's left). Argument is total jobs
   * created.
   */
  onAllAccepted: (jobsCreated: number) => void;
  /** Called after rejection — wizard should reset to step 1. */
  onRejected: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProposalTable({
  proposal,
  onAllAccepted,
  onRejected,
}: ProposalTableProps) {
  const [items, setItems] = useState<ProposedJobItem[]>(proposal.items);
  const [acceptingJobName, setAcceptingJobName] = useState<string | null>(null);
  const [acceptAllLoading, setAcceptAllLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [doneLoading, setDoneLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalAccepted, setTotalAccepted] = useState(0);

  /** Per-row Accept — creates ONLY this job, leaving the rest pending. */
  const handleAcceptItem = useCallback(
    async (jobName: string) => {
      setError(null);
      setAcceptingJobName(jobName);
      try {
        const result = await acceptProposalItem(proposal.proposalId, jobName);
        setItems((prev) => prev.filter((it) => it.jobName !== jobName));
        setTotalAccepted((prev) => {
          const next = prev + 1;
          if (result.itemsRemaining === 0) onAllAccepted(next);
          return next;
        });
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : `Failed to accept job ${jobName}.`,
        );
      } finally {
        setAcceptingJobName(null);
      }
    },
    [proposal.proposalId, onAllAccepted],
  );

  /** "Accept All" — bulk-creates every job still remaining in the list. */
  const handleAcceptAll = useCallback(async () => {
    setError(null);
    setAcceptAllLoading(true);
    try {
      const result = await acceptProposal(proposal.proposalId);
      setItems([]);
      setTotalAccepted((prev) => {
        const next = prev + result.jobsCreated;
        onAllAccepted(next);
        return next;
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to accept proposal.",
      );
    } finally {
      setAcceptAllLoading(false);
    }
  }, [proposal.proposalId, onAllAccepted]);

  const handleReject = useCallback(async () => {
    setError(null);
    setRejectLoading(true);
    try {
      await rejectProposal(proposal.proposalId);
      onRejected();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to reject proposal.",
      );
    } finally {
      setRejectLoading(false);
    }
  }, [proposal.proposalId, onRejected]);

  /**
   * "Done" — finish now with just the jobs already individually accepted.
   * Discards whatever's still left in the list (same underlying call as
   * Reject) but reports success with the jobs already created instead of
   * treating the whole proposal as rejected.
   */
  const handleDone = useCallback(async () => {
    setError(null);
    setDoneLoading(true);
    try {
      await rejectProposal(proposal.proposalId);
      onAllAccepted(totalAccepted);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to finish proposal.",
      );
    } finally {
      setDoneLoading(false);
    }
  }, [proposal.proposalId, totalAccepted, onAllAccepted]);

  const anyRowLoading = acceptingJobName !== null;

  // Every item accepted (individually and/or via Accept All) — success state.
  if (items.length === 0 && totalAccepted > 0) {
    return (
      <div className="space-y-3">
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 flex items-center gap-2">
          <CheckCheck
            size={14}
            className="text-emerald-400 flex-shrink-0"
            aria-hidden="true"
          />
          <p className="text-emerald-300 text-sm">
            {totalAccepted} job{totalAccepted !== 1 ? "s" : ""} created
            successfully.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-white/50">
          {items.length} job{items.length !== 1 ? "s" : ""} proposed
          {totalAccepted > 0 ? ` · ${totalAccepted} accepted` : ""}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void handleReject()}
            disabled={
              rejectLoading || acceptAllLoading || doneLoading || anyRowLoading
            }
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rejectLoading ? (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            ) : (
              <X size={12} aria-hidden="true" />
            )}
            Reject
          </button>
          {totalAccepted > 0 && (
            <button
              type="button"
              onClick={() => void handleDone()}
              disabled={
                doneLoading ||
                acceptAllLoading ||
                rejectLoading ||
                anyRowLoading
              }
              aria-label={`Done — finish with the ${totalAccepted} job${totalAccepted !== 1 ? "s" : ""} accepted so far, skipping the rest`}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/15 text-white border border-white/20 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {doneLoading ? (
                <Loader2
                  size={12}
                  className="animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Check size={12} aria-hidden="true" />
              )}
              Done
            </button>
          )}
          <button
            type="button"
            onClick={() => void handleAcceptAll()}
            disabled={
              acceptAllLoading || rejectLoading || doneLoading || anyRowLoading
            }
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {acceptAllLoading ? (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            ) : (
              <CheckCheck size={12} aria-hidden="true" />
            )}
            Accept All
          </button>
        </div>
      </div>

      {error !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {error}
        </p>
      )}

      {/* Proposed jobs table */}
      <div className="overflow-x-auto custom-scrollbar-x rounded-xl border border-white/10">
        <table className="w-full min-w-max">
          <thead>
            <tr className="bg-white/3">
              <th className="py-2 px-3 text-left text-xs font-medium text-white/40">
                Job Name
              </th>
              <th className="py-2 px-3 text-left text-xs font-medium text-white/40">
                Strategy
              </th>
              <th className="py-2 px-3 text-right text-xs font-medium text-white/40">
                Columns
              </th>
              <th className="py-2 px-3 text-left text-xs font-medium text-white/40">
                Driver Table
              </th>
              <th className="py-2 px-3 text-left text-xs font-medium text-white/40">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <ProposedJobRow
                key={`${item.jobName}-${idx}`}
                item={item}
                onAccept={() => void handleAcceptItem(item.jobName)}
                acceptLoading={acceptingJobName === item.jobName}
                disabled={
                  acceptAllLoading ||
                  (anyRowLoading && acceptingJobName !== item.jobName)
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-white/30">
        Click <strong className="text-white/50">Accept</strong> on a job to
        create just that job,{" "}
        <strong className="text-white/50">Accept All</strong> to create every
        remaining proposed job, <strong className="text-white/50">Done</strong>{" "}
        to finish now with just the jobs you&apos;ve accepted and skip the rest,
        or <strong className="text-white/50">Reject</strong> to discard the
        proposal.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component
// ---------------------------------------------------------------------------

interface ProposedJobRowProps {
  item: ProposedJobItem;
  /** Triggered by this row's Accept button — creates ONLY this job. */
  onAccept: () => void;
  acceptLoading: boolean;
  disabled: boolean;
}

function ProposedJobRow({
  item,
  onAccept,
  acceptLoading,
  disabled,
}: ProposedJobRowProps) {
  const strategyClass =
    STRATEGY_CHIP[item.loadStrategy] ??
    "bg-white/5 text-white/50 border border-white/10";

  return (
    <tr className="border-t border-white/5 hover:bg-white/3 transition-colors">
      <td className="py-2 px-3">
        <span className="text-xs font-mono text-white/80">{item.jobName}</span>
        {item.scdTrackedColumns?.length ? (
          <span className="ml-2 text-xs text-white/30">
            +{item.scdTrackedColumns.length} tracked
          </span>
        ) : null}
      </td>
      <td className="py-2 px-3">
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${strategyClass}`}
        >
          {STRATEGY_LABEL[item.loadStrategy] ?? item.loadStrategy}
        </span>
      </td>
      <td className="py-2 px-3 text-xs text-white/60 text-right">
        {item.columnMappings?.length ?? 0}
      </td>
      <td className="py-2 px-3 text-xs font-mono text-white/40 truncate max-w-32">
        {item.driverTable ?? "—"}
      </td>
      <td className="py-2 px-3">
        {/* Per-job Accept — creates only this job (POST .../accept-item). */}
        <button
          type="button"
          onClick={onAccept}
          disabled={acceptLoading || disabled}
          aria-label={`Accept job ${item.jobName}`}
          className="flex items-center gap-1 px-2 py-1 bg-brand-500/20 hover:bg-brand-500/30 text-brand-300 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {acceptLoading ? (
            <Loader2 size={11} className="animate-spin" aria-hidden="true" />
          ) : (
            <Check size={11} aria-hidden="true" />
          )}
          Accept
        </button>
      </td>
    </tr>
  );
}
